import React from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';

import { updateVoicemailSettings } from 'flow/actions/voicemail-actions';
import { selectHasCapability } from 'flow/selectors/ExtensionSelectors';
import SettingSelectableItem from 'flow/components/ui-components/SettingSelectableItem';
import useAppContext from 'flow/hooks/useAppContext';
import useContact from 'flow/hooks/useContact';
import useVoicemail from 'flow/hooks/useVoicemail';
import { ExtensionKey } from 'flow/types/EntityKeys';

import { Capabilities } from 'flow/utils/capability-utils';

type Option = 'email' | 'sms' | 'both' | 'none';

interface Notice {
    noticeEmail: boolean;
    noticeSMS: boolean;
}

const notificationSettings: Record<Option, Notice> = {
    email: { noticeEmail: true, noticeSMS: false },
    sms: { noticeEmail: false, noticeSMS: true },
    both: { noticeEmail: true, noticeSMS: true },
    none: { noticeEmail: false, noticeSMS: false },
};

interface Props {
    extensionKey: ExtensionKey;
    handleBack: () => void;
}

const EditVoicemailNotification = ({ extensionKey, handleBack }: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const { ownContact } = useContact();
    const { voicemailSettings } = useVoicemail(extensionKey);
    const hasCapabilitySms = useAppSelector((state) =>
        selectHasCapability(state, extensionKey, Capabilities.SMS),
    );

    const selectedItem = () =>
        Object.keys(notificationSettings).find((key: Option) => {
            const { noticeEmail, noticeSMS } = notificationSettings[key];
            return (
                noticeEmail === voicemailSettings.get('noticeEmail') &&
                noticeSMS === voicemailSettings.get('noticeSMS')
            );
        });

    const handleChange = (selected: Option) => {
        dispatch(
            updateVoicemailSettings(extensionKey, {
                key: voicemailSettings.get('key'),
                ...notificationSettings[selected],
            }),
        );
        handleBack();
    };

    return (
        <>
            <SettingSelectableItem
                isDisabled={!ownContact.get('email')}
                value="email"
                onClick={handleChange}
                selected={selectedItem()}
            >
                {i18next.t('settings.voicemail_notify_email')}
            </SettingSelectableItem>

            {hasCapabilitySms && (
                <>
                    <SettingSelectableItem
                        isDisabled={!ownContact.getIn(['mobile', 'number'])}
                        value="sms"
                        onClick={handleChange}
                        selected={selectedItem()}
                    >
                        {i18next.t('settings.voicemail_notify_sms')}
                    </SettingSelectableItem>
                    <SettingSelectableItem
                        isDisabled={
                            !ownContact.get('email') ||
                            !ownContact.getIn(['mobile', 'number'])
                        }
                        value="both"
                        onClick={handleChange}
                        selected={selectedItem()}
                    >
                        {i18next.t('settings.voicemail_notify_both')}
                    </SettingSelectableItem>
                </>
            )}

            <SettingSelectableItem
                value="none"
                onClick={handleChange}
                selected={selectedItem()}
            >
                {i18next.t('settings.voicemail_notify_none')}
            </SettingSelectableItem>
        </>
    );
};

export default EditVoicemailNotification;
